/**
 * Shown labels: the names the tables show in place of raw labels. The raw
 * label stays the identity (paths, filters and scripts keep it); a shown
 * label only changes what a title, a row, a column or a sentence says. Every
 * screen and the compiler read names through `ShownNames`, which falls back
 * to the raw label wherever no shown label is set.
 */
import { LABEL_BREAKS, nameKey, RESERVED_NAMES } from "../compiler/names"
import { CatalogueQuestion } from "../types/catalogue"
import {
  kindOf,
  LabelKind,
  labelKey,
  LabelTarget,
  labelTargetOf,
  ShownLabel,
} from "../types/tabSpecDocument"

// The target helpers live with the document type; re-exported for the screens.
export { kindOf, labelKey, LABEL_BREAKS }
export type { LabelKind, LabelTarget }

const KIND_ORDER: Record<LabelKind, number> = { variable: 0, option: 1, instance: 2 }

export const targetOf = (l: ShownLabel): LabelTarget =>
  l.option !== undefined
    ? { variable: l.variable, option: l.option }
    : l.instance !== undefined
      ? { variable: l.variable, instance: l.instance }
      : { variable: l.variable }

/** What every screen and the compiler read. Each returns the shown label, or the raw one. */
export interface ShownNames {
  variable: (variable: string) => string
  option: (variable: string, option: string) => string
  /** `fallback`: the instance's own label ("Alpha - Price"). */
  instance: (variable: string, id: string, fallback: string) => string
  /**
   * A codebook path in words: "Satisfaction//Price//Agree" gives
   * "Satisfaction: Price: Agree", each part shown. Without labels exactly
   * `path.split("//").join(": ")`.
   */
  path: (path: string) => string
  /**
   * `path` in two: the words of the variable and the instance between
   * ("Satisfaction: Price"), and of the last part, the option ("Agree");
   * `last` null when the whole path is one variable ("Heavy user"). `path`
   * is `head` alone, or both joined with ": ". `variable`: the start of the
   * path taken for the variable, as a label ("Satisfaction").
   */
  pathParts: (path: string) => { head: string; last: string | null; variable: string }
}

interface LabelIndex {
  /** `labelKey` of each target to its shown label; the first entry of a target wins. */
  byKey: Map<string, string>
  /** Each variable to the canonical text of its entries (`variableSlice`). */
  slices: Map<string, string>
}

const EMPTY_INDEX: LabelIndex = { byKey: new Map(), slices: new Map() }
/** Built once per labels array: a document write replaces the array, never edits it. */
const indexes = new WeakMap<ShownLabel[], LabelIndex>()

type SliceEntry = [LabelKind, string, string]

const compareEntries = (a: SliceEntry, b: SliceEntry): number =>
  KIND_ORDER[a[0]] - KIND_ORDER[b[0]] || (a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : 0)

const indexOf = (labels: ShownLabel[] | undefined): LabelIndex => {
  if (!labels || labels.length === 0) return EMPTY_INDEX
  const known = indexes.get(labels)
  if (known) return known
  const byKey = new Map<string, string>()
  const entries = new Map<string, SliceEntry[]>()
  labels.forEach((l) => {
    const target = targetOf(l)
    const key = labelKey(target)
    if (byKey.has(key)) return
    byKey.set(key, l.shown)
    const entry: SliceEntry = [kindOf(target), l.option ?? l.instance ?? "", l.shown]
    const list = entries.get(l.variable)
    if (list) list.push(entry)
    else entries.set(l.variable, [entry])
  })
  const slices = new Map<string, string>()
  entries.forEach((list, variable) =>
    slices.set(variable, JSON.stringify([...list].sort(compareEntries)))
  )
  const index = { byKey, slices }
  indexes.set(labels, index)
  return index
}

/**
 * `seen` (optional) collects every variable whose names were read; for a
 * path, every start of it (a variable of the data can be named by a nested
 * path, so any start may be one).
 */
export const shownNames = (labels: ShownLabel[] | undefined, seen?: Set<string>): ShownNames => {
  const { byKey, slices } = indexOf(labels)
  // A stored label is kept as it was written; it is shown trimmed.
  const look = (target: LabelTarget, fallback: string): string =>
    byKey.size === 0 ? fallback : byKey.get(labelKey(target))?.trim() || fallback

  const variable = (v: string): string => {
    seen?.add(v)
    return look({ variable: v }, v)
  }
  const option = (v: string, o: string): string => {
    seen?.add(v)
    return look({ variable: v, option: o }, o)
  }
  const instance = (v: string, id: string, fallback: string): string => {
    seen?.add(v)
    return look({ variable: v, instance: id }, fallback)
  }
  // The variable is the first part, or the longest start of the path that
  // has shown labels of its own (a variable of the data nested in the
  // codebook is named by its whole path); after it the last part is an
  // option (a leaf), and the parts between them one instance id (a grid row,
  // an iteration, or both). Without labels: the parts joined with ": ".
  const pathParts = (p: string): { head: string; last: string | null; variable: string } => {
    const parts = p.split("//")
    if (seen) for (let i = 1; i <= parts.length; i++) seen.add(parts.slice(0, i).join("//"))
    let at = 1
    if (slices.size > 0)
      for (let i = parts.length; i > 1; i--)
        if (slices.has(parts.slice(0, i).join("//"))) {
          at = i
          break
        }
    const v = parts.slice(0, at).join("//")
    const rest = parts.slice(at)
    const between = rest.slice(0, -1)
    return {
      head: [
        look({ variable: v }, parts.slice(0, at).join(": ")),
        ...(between.length > 0 ? [instance(v, between.join("//"), between.join(": "))] : []),
      ].join(": "),
      last: rest.length > 0 ? option(v, rest[rest.length - 1]) : null,
      variable: v,
    }
  }
  const path = (p: string): string => {
    const { head, last } = pathParts(p)
    return last === null ? head : `${head}: ${last}`
  }
  return { variable, option, instance, path, pathParts }
}

/** The raw labels, everywhere. */
export const RAW_NAMES: ShownNames = shownNames(undefined)

/** A canonical text of every entry of `variable`; "" when it has none. Cached per labels array. */
export const variableSlice = (labels: ShownLabel[] | undefined, variable: string): string =>
  indexOf(labels).slices.get(variable) ?? ""

export interface LabelChange {
  target: LabelTarget
  shown: string | null
}

const entryOf = (target: LabelTarget, shown: string): ShownLabel => ({
  variable: target.variable,
  ...(target.option !== undefined
    ? { option: target.option }
    : target.instance !== undefined
      ? { instance: target.instance }
      : {}),
  shown,
})

const compareStrings = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

/** By variable, then the variable itself, its options, its instances, each by code unit order. */
const compareLabels = (a: ShownLabel, b: ShownLabel): number =>
  compareStrings(a.variable, b.variable) ||
  KIND_ORDER[kindOf(a)] - KIND_ORDER[kindOf(b)] ||
  compareStrings(a.option ?? a.instance ?? "", b.option ?? b.instance ?? "")

/**
 * The list after `changes`: trimmed; null, empty, or equal to `raw(target)`
 * removes the entry; canonical order (by variable, then the variable itself,
 * its options, its instances, each by code unit order). undefined when empty.
 * A change whose target a document cannot hold (`labelTargetOf`: an empty
 * variable, option or instance, or both an option and an instance) is skipped.
 */
export const applyLabelChanges = (
  labels: ShownLabel[] | undefined,
  changes: LabelChange[],
  raw: (t: LabelTarget) => string
): ShownLabel[] | undefined => {
  const byKey = new Map<string, ShownLabel>()
  ;(labels ?? []).forEach((l) => {
    const key = labelKey(targetOf(l))
    if (!byKey.has(key)) byKey.set(key, l)
  })
  changes.forEach((change) => {
    const target = labelTargetOf(change.target)
    if (!target) return
    const { shown } = change
    const key = labelKey(target)
    const text = shown?.trim() ?? ""
    if (!text || text === raw(target)) byKey.delete(key)
    else byKey.set(key, entryOf(target, text))
  })
  const out = [...byKey.values()].sort(compareLabels)
  return out.length > 0 ? out : undefined
}

/**
 * The rule for a name that is only shown: why `text` cannot be one, or null.
 * It never reaches the engine, so quotes and brackets are fine; a line break,
 * "//", "Totals" and "Base" are not, and an item (a category, row, cell or
 * iteration) cannot be shown like another item of the same variable
 * (`others`: the names they are shown under). An empty text is not judged
 * here: the callers treat it as a reset.
 */
export const refusedShownLabel = (
  text: string,
  others: string[],
  item: { word: "category" | "row" | "cell" | "iteration"; owner: string } | null
): string | null => {
  const trimmed = text.trim()
  if (!trimmed) return null
  if (LABEL_BREAKS.test(trimmed)) return `A label cannot hold a line break or "//".`
  if (RESERVED_NAMES.includes(nameKey(trimmed)))
    return `"${trimmed}" is a name the finished table keeps for itself.`
  if (!item) return null
  const other = others.find((o) => nameKey(o) === nameKey(trimmed))
  return other === undefined
    ? null
    : `Another ${item.word} of ${item.owner} is already shown as "${other.trim()}".`
}

/** The word for the instances of `q` in the Labels drawer and the sheet. */
export const instanceWord = (q: CatalogueQuestion): "row" | "cell" | "iteration" =>
  q.type === "grid-number" ? "cell" : q.type.startsWith("grid-") ? "row" : "iteration"
